import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { mockData } from '../data/mock';

const Services = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary to-primary-dark pt-28" style={{ paddingTop: '6.5rem' }}>
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="hero-text mb-6">Our Services</h1>
            <p className="text-xl text-gray-800 leading-relaxed">
              Comprehensive design and development solutions to elevate your brand and drive business growth through creative excellence.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-white">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="section-header mb-4">What We Do</h2>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              From concept to execution, we provide end-to-end creative solutions tailored to your unique needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {mockData.services.map((service, index) => (
              <div key={index} className="card-white group hover:shadow-xl transition-all duration-300">
                <div className="flex items-start space-x-6">
                  <div className="w-16 h-16 bg-black rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                    <service.icon size={24} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="subsection-header mb-4">{service.title}</h3>
                    <p className="text-gray-700 mb-6 leading-relaxed">{service.description}</p>
                    
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-3">What's Included:</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {service.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center space-x-2">
                            <Check size={16} className="text-green-600 flex-shrink-0" />
                            <span className="text-gray-600 text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <Link 
                      to="/contact" 
                      className="btn-secondary text-sm group-hover:bg-black group-hover:text-white transition-colors"
                    >
                      Get Quote <ArrowRight size={16} />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="section-header mb-4">Our Process</h2>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              A proven methodology that ensures every project is delivered on time, on budget, and exceeds expectations
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Discovery',
                description: 'We dive deep into your business goals, target audience, and project requirements to create a solid foundation.'
              },
              {
                step: '02',
                title: 'Strategy',
                description: 'Based on our research, we develop a comprehensive strategy that aligns with your objectives and market needs.'
              },
              {
                step: '03',
                title: 'Design',
                description: 'Our creative team brings the strategy to life through innovative design concepts and visual solutions.'
              },
              {
                step: '04',
                title: 'Delivery',
                description: 'We deliver polished, tested solutions and provide ongoing support to ensure continued success.'
              }
            ].map((process, index) => (
              <div key={index} className="text-center group">
                <div className="w-20 h-20 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold group-hover:scale-110 transition-transform">
                  {process.step}
                </div>
                <h3 className="font-semibold text-xl mb-3">{process.title}</h3>
                <p className="text-gray-600 leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-gray-50">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="section-header mb-4">Why Choose NS7VISUALS?</h2>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              We combine creativity, strategy, and technical expertise to deliver results that matter
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Expert Team',
                description: 'Our diverse team brings years of experience across different industries and design disciplines.',
                icon: '👥'
              },
              {
                title: 'Proven Results',
                description: '10+ successful projects with measurable impact on our clients\' business growth and brand recognition.',
                icon: '📈'
              },
              {
                title: 'End-to-End Service',
                description: 'From initial concept to final implementation, we handle every aspect of your design needs.',
                icon: '🎯'
              },
              {
                title: 'Client-Focused',
                description: 'Your success is our priority. We work closely with you throughout the entire project lifecycle.',
                icon: '🤝'
              },
              {
                title: 'Latest Technology',
                description: 'We stay ahead of design trends and use cutting-edge tools to deliver modern, future-proof solutions.',
                icon: '⚡'
              },
              {
                title: 'Ongoing Support',
                description: 'Our relationship doesn\'t end at delivery. We provide continued support and maintenance services.',
                icon: '🛡️'
              }
            ].map((benefit, index) => (
              <div key={index} className="card-white text-center group">
                <div className="text-4xl mb-4">{benefit.icon}</div>
                <h3 className="font-semibold text-lg mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-black text-white">
        <div className="container text-center">
          <h2 className="section-headers mb-4 text-white">Ready to Get Started?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Let's discuss your project and explore how we can help bring your vision to life
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-black px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Start Your Project
            </Link>
            <Link to="/portfolio" className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-black transition-colors">
              View Our Work
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;